import { Show, useContext } from 'solid-js';
import { Button } from '../../ui-components/button/button';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { QuantityStepper } from '../../ui-components/quantity-stepper/quantity-stepper';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { MappedOrderDetails } from './basket';
import { ProductData, Specification } from '../product/product-types';
import { PlusIcon } from '../icons-library/plus';
import { ShoppingBasketIcon } from '../icons-library/shopping-basket';
import { BasketContext } from './basket-context-provider';
import { getImageUrl } from '../product/product-shop-toolbar/product-shop-toolbar';
import { cviSemantic } from '../../style/theme';

/* Same DS quantity stepper as the product page's shop toolbar (Figma 173:10917), on the
   neutral surface because the basket rows sit on the white page. */
export const AmountControls = (props: { amount: number; onDecreaseClick: () => void; onIncreaseClick: () => void }) => (
    <QuantityStepper
        amount={props.amount}
        onDecrease={props.onDecreaseClick}
        onIncrease={props.onIncreaseClick}
        minAmount={1}
        surface="neutral"
    />
);

export const OrderLineQuantity = (props: { orderDetails: MappedOrderDetails }) => {
    const { decrementQuantity, incrementQuantity } = useContext(BasketContext);

    return (
        <AmountControls
            amount={props.orderDetails.line.amount}
            onDecreaseClick={() => decrementQuantity(props.orderDetails.line)}
            onIncreaseClick={() => incrementQuantity(props.orderDetails.line)}
        />
    );
};

export const Header = () => {
    const { localize } = useContext(AppContext);

    return (
        <h1 class="m-0 heading-3xl font-cvi-medium text-cvi-fg-default">
            {localize('checkout', 'Checkout')}
        </h1>
    );
};

export const BasketLoading = () => {
    const { productsListingPage, localize } = useContext(AppContext);
    const { basket } = useContext(BasketContext);

    return (
        <div class="flex flex-col items-start gap-cvi-6 py-cvi-9">
            <Show
                when={!basket.loading}
                fallback={<LoadingSpinner color={cviSemantic.fgBrand} />}
            >
                <p class="m-0 heading-xl font-cvi-medium text-cvi-fg-default">
                    {localize('basket-is-empty', 'Your basket is empty')}
                </p>
                <Button
                    label={localize('back-to-products', 'Back to products')}
                    isLink
                    url={productsListingPage.url}
                    variant='primary'
                    iconStart="arrow-left"
                    noBlockSpacing
                />
            </Show>
        </div>
    );
};

export const RemoveButton = (props: { orderDetails: MappedOrderDetails; }) => {
    const { localize } = useContext(AppContext);
    const { removeOrderLine } = useContext(BasketContext);

    return (
        <Button
            label={localize('remove', 'Remove')}
            variant='ghost'
            small
            noBlockSpacing
            onClick={() => removeOrderLine(props.orderDetails.line)}
        />
    );
};

export const AddToBasketButton = (props: { product: ProductData; specification: Specification }) => {
    const { localize } = useContext(AppContext);
    const { addNewOrderLine } = useContext(BasketContext);

    const image = getImageUrl(props.product.images);

    return (
        <button
            class="icon-button-secondary-neutral gap-cvi-3 px-cvi-5 py-cvi-3"
            aria-label={localize('add-to-basket', 'Add to basket')}
            onClick={() => addNewOrderLine({
                id: props.product.id,
                externalId: props.product.externalId,
                postTitle: props.product.post_title,
                slug: props.product.slug,
                type: props.product.type,
                amount: 1,
                skuRefNumber: props.specification.refNumber,
                skuDescription: props.specification.description || '',
                skuName: props.specification.name,
                skuQuantity: parseInt(props.specification.quantity || '1'),
                imageUrl: image?.imageUrl,
                imageRatio: image?.imageRatio,
            })}
        >
            <PlusIcon size='small' ariaHidden fill='currentColor' />
            <ShoppingBasketIcon size='medium' ariaHidden fill='currentColor' />
        </button>
    );
};
